import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import StraightenIcon from '@mui/icons-material/Straighten';
import { listAvailableJobs, acceptBooking, declineBooking } from '../services/internApi';
import { formatDate } from '../utils/formatters';
import ConfirmDialog from '../components/common/ConfirmDialog';

const AUTO_REFRESH_MS = 30000;

export default function AvailableJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [meta, setMeta] = useState({ radius_miles: null, intern_zip: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [actionBooking, setActionBooking] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  const loadJobs = useCallback((isAutoRefresh = false) => {
    if (isAutoRefresh) setRefreshing(true);
    else setLoading(true);

    return listAvailableJobs()
      .then((data) => {
        setJobs(data.jobs || []);
        setMeta({
          radius_miles: data.radius_miles,
          intern_zip: data.intern_zip,
          not_eligible_reason: data.not_eligible_reason,
          intern_status: data.intern_status,
          gating_message: data.message,
        });
        setLastUpdated(new Date());
        setError('');
      })
      .catch((err) => setError(err?.response?.data?.error || 'Failed to load jobs'))
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  }, []);

  // Initial load + auto-refresh interval
  useEffect(() => {
    loadJobs();
    intervalRef.current = setInterval(() => loadJobs(true), AUTO_REFRESH_MS);
    return () => clearInterval(intervalRef.current);
  }, [loadJobs]);

  const handleAccept = async () => {
    if (!actionBooking) return;
    setActionLoading(true);
    try {
      await acceptBooking(actionBooking.booking.booking_id);
      setSnack({ open: true, message: 'Booking accepted! Check My Bookings for details.', severity: 'success' });
      setActionBooking(null);
      loadJobs();
    } catch (err) {
      const data = err?.response?.data || {};
      const msg = data.error || 'Failed to accept booking';
      const status = err?.response?.status;
      // Special handling for "taken" 409 error
      if (status === 409 || data.code === 'BOOKING_TAKEN') {
        setSnack({
          open: true,
          message: msg,
          severity: 'warning',
        });
        setActionBooking(null);
        loadJobs();
      } else if (status === 429) {
        setSnack({ open: true, message: msg, severity: 'warning' });
        setActionBooking(null);
      } else {
        setSnack({ open: true, message: msg, severity: 'error' });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!actionBooking) return;
    setActionLoading(true);
    try {
      await declineBooking(actionBooking.booking.booking_id, 'Not available');
      setSnack({ open: true, message: 'Declined. Removed from your list.', severity: 'info' });
      setActionBooking(null);
      loadJobs();
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.error || 'Failed to decline', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>Available Jobs</Typography>
          <Typography variant="body2" color="text.secondary">
            Within {meta.radius_miles || 75} miles of your ZIP. First to accept wins. {' '}
            <Box component="a" href="/profile" sx={{ color: 'primary.main', textDecoration: 'none' }}>
              Change radius
            </Box>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
          <Tooltip title="Refresh now (auto-refreshes every 30s)">
            <IconButton onClick={() => loadJobs(true)} size="small">
              {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="caption" color="text.secondary">
          Showing {jobs.length} job{jobs.length === 1 ? '' : 's'}. Client name and address are masked until you accept.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {meta.not_eligible_reason === 'onboarding_incomplete' ? (
        <Paper sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center', background: 'linear-gradient(135deg, #f0f9ff, #ede9fe)' }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>Almost there!</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: 'auto' }}>
            {meta.gating_message || 'You need to complete onboarding before jobs become visible.'} Head to your dashboard to see what's next.
          </Typography>
          <Button variant="contained" href="/" size="large">Go to Dashboard</Button>
        </Paper>
      ) : meta.not_eligible_reason === 'stripe_setup_required' ? (
        <Paper sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center', background: 'linear-gradient(135deg, #fef3c7, #fee2e2)' }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>Set up payouts to start working</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: 'auto' }}>
            {meta.gating_message || 'You need to finish Stripe onboarding before you can accept jobs.'}
          </Typography>
          <Button variant="contained" href="/payouts/setup" size="large">
            Go to Payment Setup
          </Button>
        </Paper>
      ) : meta.not_eligible_reason === 'suspended' ? (
        <Paper sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>Account suspended</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
            {meta.gating_message || 'Your account is suspended. Contact admin to restore access.'}
          </Typography>
        </Paper>
      ) : jobs.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">No available jobs right now</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            We'll notify you when new assessments come in within {meta.radius_miles || 75} miles. The page auto-refreshes every 30 seconds.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {jobs.map((job) => (
            <Grid item xs={12} md={6} key={job.booking_id}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {job.service_type === 'business' ? 'Business Assessment' : 'Home Assessment'}
                    </Typography>
                    <Chip label={`$${job.payout_amount || 140} payout`} size="small" color="success" sx={{ mt: 0.5 }} />
                  </Box>
                  <Chip label="Open" size="small" color="warning" />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventIcon fontSize="small" color="action" />
                    <Typography variant="body2">{formatDate(job.time_slot)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {job.client_first_name}{job.client_last_initial ? ` ${job.client_last_initial}` : ''}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (full name shown after you accept)
                      </Typography>
                    </Typography>
                  </Box>
                  {(job.client_city || job.client_state) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {[job.client_city, job.client_state].filter(Boolean).join(', ')}
                      </Typography>
                    </Box>
                  )}
                  {job.distance_miles != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StraightenIcon fontSize="small" color="action" />
                      <Typography variant="body2">{job.distance_miles} mi from you</Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon />}
                    fullWidth
                    onClick={() => setActionBooking({ booking: job, action: 'accept' })}
                  >
                    Accept
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<CloseIcon />}
                    onClick={() => setActionBooking({ booking: job, action: 'decline' })}
                  >
                    Decline
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <ConfirmDialog
        open={actionBooking?.action === 'accept'}
        title="Accept this booking?"
        message={`You'll be committing to perform this ${actionBooking?.booking?.service_type === 'business' ? 'business' : 'home'} assessment on ${formatDate(actionBooking?.booking?.time_slot)}${actionBooking?.booking?.distance_miles ? ` (${actionBooking.booking.distance_miles} mi away)` : ''}. Make sure you can be there.`}
        confirmText="Yes, accept"
        confirmColor="success"
        onConfirm={handleAccept}
        onCancel={() => setActionBooking(null)}
        loading={actionLoading}
      />

      <ConfirmDialog
        open={actionBooking?.action === 'decline'}
        title="Decline this booking?"
        message="It will be removed from your list and stay open for other interns. No penalty for declining."
        confirmText="Yes, decline"
        confirmColor="inherit"
        onConfirm={handleDecline}
        onCancel={() => setActionBooking(null)}
        loading={actionLoading}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
